/**
 * Created on Fri Jun 12 2026 4:08:23 PM
 *
 * *************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2026
 * All Rights Reserved
 *
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ************************************************************************************/


import { computeElementId } from './elementIdentity';

const normalizeElementId = (id) => String(id || '').replace(/#\d+$/, '');

// Collect candidate elements for matching saved layout/hidden entries to DOM nodes.
// We intentionally collect EVERY element node (not only "editable"-typed ones) so that
// generic containers / boxes / shapes the author moved or DELETED in the builder can be
// matched and applied (e.g. a hidden plain <div>). Matching is still keyed by the exact
// computed element id, so widening the candidate set only adds correct matches.
function collectCandidateElements(sectionEl) {
  const candidates = [];

  if (typeof sectionEl.querySelectorAll === 'function') {
    const all = sectionEl.querySelectorAll('*');
    for (let i = 0; i < all.length; i += 1) {
      candidates.push(all[i]);
    }
    return candidates;
  }

  const stack = [];
  const children = sectionEl.children || [];
  for (let i = children.length - 1; i >= 0; i -= 1) stack.push(children[i]);

  while (stack.length > 0) {
    const node = stack.pop();
    if (node && node.nodeType === 1) {
      candidates.push(node);
      const kids = node.children || [];
      for (let i = kids.length - 1; i >= 0; i -= 1) stack.push(kids[i]);
    }
  }
  return candidates;
}

export function matchElements(sectionEl, entries) {
  if (!sectionEl || sectionEl.nodeType !== 1) return [];
  if (!entries || typeof entries !== 'object') return [];

  const elementIds = Object.keys(entries);
  if (elementIds.length === 0) return [];

  const nodeById = new Map();
  const candidates = collectCandidateElements(sectionEl);
  for (let i = 0; i < candidates.length; i += 1) {
    const node = candidates[i];
    const id = computeElementId(sectionEl, node);
    if (id && !nodeById.has(id)) nodeById.set(id, node);
    const stableId = normalizeElementId(id);
    if (stableId && !nodeById.has(stableId)) nodeById.set(stableId, node);
  }

  const matched = [];
  for (let i = 0; i < elementIds.length; i += 1) {
    const elementId = elementIds[i];
    const node = nodeById.get(elementId) || nodeById.get(normalizeElementId(elementId));
    if (node) {
      matched.push({ elementId, node, layout: entries[elementId] });
    }
  }
  return matched;
}

export default matchElements;
